import {
  LoadingPage,
  PaginatedTable,
  PaginatedTableProps,
} from '@act/storybook';
import { TextField } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { clientAppSelectors, getAllClients } from 'redux/slices/clientSlice';
import { useAppDispatch } from 'redux/store-hooks';
import { ClientDto } from '@act/shared/data-transfer-objects';
import styled from 'styled-components';
import { getAllConsentTerms } from 'redux/slices/consent.slice';
import { CreateEditCustomer } from './CreateEditCustomer';
import { getAllInternalNumbers } from 'redux/slices/internalPhoneNumberSlice';
import { actingUserAppSelectors } from 'redux/slices/actingUserSlice';
import { hasPermission } from 'utilities/hasPermissions';
import { AdminToolSkeleton } from '../AdminToolSkeleton';
import { CustomerTableColumns } from './CustomerTableColumns';
import { IndexChildContainer } from 'react-components/member-index/indexContainer';
import { AdminDrawer } from '../AdminDrawer';
import AddIcon from '@mui/icons-material/Add';
import { CustomerDetails } from './CustomerDetails';
import { useDialog } from '../../../../stories/dialogs/DialogProvider';

export const FieldDiv = styled.div`
  height: 76px;
  align-self: stretch;
  min-width: 410px;
`;
export const CustomerView = () => {
  const allClients = clientAppSelectors.useSelectAllClients();
  const actingUser = actingUserAppSelectors.useSelectActingUser();
  const dispatch = useAppDispatch();
  const [selectedClient, setSelectedClient] = useState<ClientDto | null>(null);
  const [editMode, setSetEditMode] = useState(false);
  const [createNewCustomer, setCreateNewCustomer] = useState(false);
  useEffect(() => {
    dispatch(getAllClients({ params: { includeConsents: true } }));
    dispatch(getAllConsentTerms({}));
    dispatch(getAllInternalNumbers({}));
  }, []);

  const clientList = useMemo(
    () => allClients.ids.map(id => allClients.entities[id]),
    [allClients]
  );

  const setFormOpen = (value: boolean) => {
    if (!value) {
      setSelectedClient(null);
      setSetEditMode(false);
    }
  };

  const [customerDetailsDialogCustomer, setCustomerDetailsDialogCustomer] =
    useState<ClientDto>();
  const { openDialog } = useDialog(
    CustomerDetails,
    () => ({
      client: customerDetailsDialogCustomer,
      title: `Customer Details for ${customerDetailsDialogCustomer?.name}`,
    }),
    [customerDetailsDialogCustomer]
  );

  const openCustomerDetails = (client: ClientDto) => {
    setCustomerDetailsDialogCustomer(client);
    openDialog();
  };

  const columns = useMemo(() => {
    return CustomerTableColumns({ openCustomerDetails });
  }, []);

  const [searchTerm, setSearchTerm] = useState('');
  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) {
      return clientList;
    }
    return clientList.filter(client => {
      const clientData = `${client.name}`.toLowerCase();
      return clientData.includes(searchTerm.toLowerCase());
    });
  }, [clientList, searchTerm]);

  const CUSTOMER_ADMIN_TABLE_PROPS: PaginatedTableProps<ClientDto> = {
    data: filteredData,
    columnConfig: columns,
    inMemoryPagination: true,
    onRowClick: client => {
      setSelectedClient(client);
      setSetEditMode(true);
    },
  };

  if (allClients.ids.length === 0) {
    return <LoadingPage />;
  }

  const canCreateCustomer = hasPermission(actingUser, 'CAN_MODIFY_CLIENTS');

  return (
    <AdminToolSkeleton
      title="Customers"
      toolDescription="Customer record and market information can be viewed and updated
            here."
      buttonContent="Add New Customer"
      variant="contained"
      onClick={() => {
        setCreateNewCustomer(true);
      }}
      startIcon={<AddIcon sx={{ ml: 1 }} />}
    >
      <TextField
        label="Search by customer name"
        variant="outlined"
        value={searchTerm}
        onChange={e => setSearchTerm(e.target.value)}
        fullWidth
        margin="normal"
        sx={{ backgroundColor: '#ffffff' }}
      />
      <IndexChildContainer>
        <PaginatedTable {...CUSTOMER_ADMIN_TABLE_PROPS} />
      </IndexChildContainer>
      <AdminDrawer
        open={createNewCustomer || editMode}
        setOpen={createNewCustomer ? setCreateNewCustomer : setFormOpen}
        title={
          createNewCustomer ? 'Create Customer' : `Edit ${selectedClient?.name}`
        }
      >
        {(createNewCustomer || editMode) && (
          <CreateEditCustomer
            stopCreating={() => setCreateNewCustomer(false)}
            createMode={createNewCustomer}
            canEdit={canCreateCustomer}
            customerDetail={selectedClient ? selectedClient : undefined}
          />
        )}
      </AdminDrawer>
    </AdminToolSkeleton>
  );
};
